import axios from 'axios';
import type { MatterSummary, MatterDetail, Clause, MatterChatResult, MatterVersion, CompareResponse, RiskItem, ProtocolPreview } from '../types';

const API_URL = '';

export async function listMatters() {
  const res = await axios.get<MatterSummary[]>(`${API_URL}/api/matters`);
  return res.data;
}

interface CreateMatterInput {
  title: string;
  counterparty: string;
  role: string;
  contract_type?: string;
  file?: File | null;
  text?: string;
}

export async function createMatter(input: CreateMatterInput, signal?: AbortSignal) {
  const fd = new FormData();
  fd.append('title', input.title);
  fd.append('counterparty', input.counterparty);
  fd.append('role', input.role);
  fd.append('contract_type', input.contract_type || '');
  if (input.file) {
    fd.append('file', input.file);
  } else {
    fd.append('text', input.text || '');
  }
  const res = await axios.post<MatterDetail>(`${API_URL}/api/matters`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 1_800_000,  // heavy: ingest + full risk analysis (MAP/REDUCE waves)
    signal,
  });
  return res.data;
}

interface FromAnalysisInput {
  text: string;
  role: string;
  contract_type?: string;
  risks: RiskItem[];
  summary?: string;
  overall_risk?: string;
  warning?: string;
}

export async function createMatterFromAnalysis(input: FromAnalysisInput, signal?: AbortSignal) {
  const res = await axios.post<MatterDetail>(`${API_URL}/api/matters/from-analysis`, {
    text: input.text,
    role: input.role,
    contract_type: input.contract_type || '',
    risks: input.risks,
    summary: input.summary || '',
    overall_risk: input.overall_risk || '',
    warning: input.warning || '',
  }, { timeout: 1_800_000, signal });
  return res.data;
}

export async function getMatter(id: number) {
  const res = await axios.get<MatterDetail>(`${API_URL}/api/matters/${id}`);
  return res.data;
}

export async function deleteMatter(id: number) {
  await axios.delete(`${API_URL}/api/matters/${id}`);
}

export async function patchClause(
  matterId: number,
  clauseId: number,
  patch: { status?: string; current_text?: string; base_text?: string; user_note?: string; include_in_protocol?: boolean; is_risk?: boolean },
) {
  const res = await axios.patch<Clause>(
    `${API_URL}/api/matters/${matterId}/clauses/${clauseId}`,
    patch,
  );
  return res.data;
}

export async function patchMatter(
  matterId: number,
  patch: { status?: string; title?: string; counterparty?: string; contract_number?: string; contract_date?: string; protocol_intro?: string; manager_letter?: string },
) {
  const res = await axios.patch<MatterDetail>(`${API_URL}/api/matters/${matterId}`, patch);
  return res.data;
}

export function protocolUrl(matterId: number) {
  return `${API_URL}/api/matters/${matterId}/protocol.docx`;
}

export async function getProtocolPreview(
  matterId: number,
  overrides?: { intro?: string; contract_number?: string; contract_date?: string },
): Promise<ProtocolPreview> {
  const res = await axios.get<ProtocolPreview>(`${API_URL}/api/matters/${matterId}/protocol`, {
    params: overrides,
  });
  return res.data;
}

export async function getProtocolBlob(matterId: number): Promise<{ blob: Blob; name: string }> {
  const res = await axios.get(`${API_URL}/api/matters/${matterId}/protocol.docx`, { responseType: 'blob' });
  const cd = (res.headers['content-disposition'] as string) || '';
  const m = /filename\*=UTF-8''([^;]+)/.exec(cd);
  const name = m ? decodeURIComponent(m[1]) : `Протокол разногласий - ${matterId}.docx`;
  return { blob: res.data as Blob, name };
}

export async function listVersions(matterId: number) {
  const res = await axios.get<MatterVersion[]>(`${API_URL}/api/matters/${matterId}/versions`);
  return res.data;
}

export async function addVersion(
  matterId: number,
  input: { file?: File | null; text?: string; label?: string; kind: string; source: string },
) {
  const fd = new FormData();
  if (input.file) fd.append('file', input.file); else fd.append('text', input.text || '');
  fd.append('label', input.label || '');
  fd.append('kind', input.kind);
  fd.append('source', input.source);
  const res = await axios.post<MatterVersion>(`${API_URL}/api/matters/${matterId}/versions`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' }, timeout: 1_800_000,  // OCR of large scans
  });
  return res.data;
}

export async function saveProtocolVersion(matterId: number) {
  const res = await axios.post<MatterVersion>(`${API_URL}/api/matters/${matterId}/protocol/save-version`);
  return res.data;
}

export async function compareVersions(matterId: number, versionA: number, versionB: number) {
  const res = await axios.post<CompareResponse>(`${API_URL}/api/matters/${matterId}/compare`,
    { version_a: versionA, version_b: versionB }, { timeout: 600_000 });
  return res.data;
}

export async function analyzeVersion(matterId: number, versionId: number) {
  const res = await axios.post<MatterDetail>(
    `${API_URL}/api/matters/${matterId}/versions/${versionId}/analyze`, {}, { timeout: 1_800_000 });
  return res.data;
}

export async function generateManagerLetter(matterId: number) {
  const res = await axios.post<{ text: string }>(
    `${API_URL}/api/matters/${matterId}/manager-letter`, {}, { timeout: 600_000 });
  return res.data;
}

export async function downloadManagerLetterDocx(matterId: number, text: string) {
  const res = await axios.post(`${API_URL}/api/matters/${matterId}/manager-letter.docx`,
    { text }, { responseType: 'blob' });
  const cd = (res.headers['content-disposition'] as string) || '';
  const m = /filename\*=UTF-8''([^;]+)/.exec(cd);
  const name = m ? decodeURIComponent(m[1]) : `Письмо менеджерам - ${matterId}.docx`;
  const url = window.URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export async function matterChat(matterId: number, message: string, signal?: AbortSignal) {
  const res = await axios.post<MatterChatResult>(
    `${API_URL}/api/matters/${matterId}/chat`,
    { message },
    { timeout: 600_000, signal },
  );
  return res.data;
}

export async function applyMatterChat(matterId: number, messageId: number, accept: number[]) {
  const res = await axios.post<MatterDetail>(
    `${API_URL}/api/matters/${matterId}/chat/apply`,
    { message_id: messageId, accept },
    { timeout: 600_000 },
  );
  return res.data;
}
